import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Plus, Search, Edit, Trash2, Eye, X, CheckCircle2, Clock, AlertCircle } from 'lucide-react';

interface PaymentStatus {
  id: number;
  name: string;
  nameAr: string;
  description?: string;
  color?: string;
  isActive: boolean;
  sortOrder: number;
}

export default function PaymentStatusesPage() {
  const [statuses, setStatuses] = useState<PaymentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<PaymentStatus | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<PaymentStatus | null>(null);
  const [form, setForm] = useState({
    name: '', nameAr: '', description: '', color: '#FFA500', isActive: true, sortOrder: '1'
  });

  useEffect(() => { loadStatuses(); }, []);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/paymentstatuses');
      if (res.data?.success) setStatuses(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingStatus(null);
    setForm({ name: '', nameAr: '', description: '', color: '#FFA500', isActive: true, sortOrder: '1' });
    setShowModal(true);
  };

  const openEdit = (s: PaymentStatus) => {
    setEditingStatus(s);
    setForm({
      name: s.name || '',
      nameAr: s.nameAr || '',
      description: s.description || '',
      color: s.color || '#FFA500',
      isActive: s.isActive,
      sortOrder: s.sortOrder.toString()
    });
    setShowModal(true);
  };

  const openView = (s: PaymentStatus) => { setSelectedStatus(s); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        nameAr: form.nameAr,
        description: form.description,
        color: form.color,
        isActive: form.isActive,
        sortOrder: parseInt(form.sortOrder) || 1
      };
      if (editingStatus) {
        await api.put('/api/paymentstatuses/' + editingStatus.id, payload);
        alert("تم تحديث حالة الدفع بنجاح");
      } else {
        await api.post('/api/paymentstatuses', payload);
        alert("تم إضافة حالة الدفع بنجاح");
      }
      setShowModal(false);
      loadStatuses();
    } catch (err: any) {
      alert(err.response?.data?.message || "حدث خطأ");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("هل أنت متأكد من حذف حالة الدفع؟")) return;
    try {
      await api.delete('/api/paymentstatuses/' + id);
      loadStatuses();
      alert("تم الحذف بنجاح");
    } catch (err: any) {
      alert(err.response?.data?.message || "حدث خطأ");
    }
  };

  const filtered = statuses.filter(s =>
    (s.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.nameAr || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeStatuses = statuses.filter(s => s.isActive).length;
  const inputClass = "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 transition";

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>حالات الدفع</span>
            <p className="text-sm text-gray-500 font-normal mt-1">إدارة حالات الدفع للفواتير والعمليات المالية</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>إضافة حالة جديدة</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><CreditCard className="w-5 h-5 text-purple-600" /></div></div>
          <p className="text-sm text-gray-500">إجمالي الحالات</p>
          <p className="text-2xl font-bold text-gray-900">{statuses.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">الحالات النشطة</p>
          <p className="text-2xl font-bold text-green-700">{activeStatuses}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><Clock className="w-5 h-5 text-gray-600" /></div></div>
          <p className="text-sm text-gray-500">الحالات غير النشطة</p>
          <p className="text-2xl font-bold text-gray-700">{statuses.length - activeStatuses}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="بحث باسم الحالة..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 transition" />
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div><p className="text-gray-500 mt-4">جاري التحميل...</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">لا توجد حالات دفع</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الترتيب</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اسم الحالة</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الوصف</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اللون</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الحالة</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((status) => (
                    <tr key={status.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5"><span className="font-mono font-bold text-purple-600">{status.sortOrder}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: status.color || '#FFA500' }}><CreditCard className="w-5 h-5" /></div>
                          <div>
                            <p className="font-semibold text-gray-900">{status.nameAr}</p>
                            <p className="text-xs text-gray-500">{status.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5"><span className="text-gray-700 text-sm">{status.description || '-'}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg border-2 border-gray-200" style={{ backgroundColor: status.color || '#FFA500' }}></div>
                          <span className="font-mono text-xs text-gray-600">{status.color || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{status.isActive ? 'نشط' : 'غير نشط'}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openView(status)} className="p-2 hover:bg-purple-50 rounded-lg text-gray-500 hover:text-purple-600 transition" title="عرض"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(status)} className="p-2 hover:bg-purple-50 rounded-lg text-gray-500 hover:text-purple-600 transition" title="تعديل"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(status.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title="حذف"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingStatus ? 'تعديل حالة الدفع' : 'إضافة حالة دفع جديدة'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>الاسم بالإنجليزية *</Label><Input value={form.name} onChange={(e: any) => setForm({...form, name: e.target.value})} required className={inputClass} placeholder="Unpaid" /></div>
                <div><Label>الاسم بالعربية *</Label><Input value={form.nameAr} onChange={(e: any) => setForm({...form, nameAr: e.target.value})} required className={inputClass} placeholder="غير مدفوع" /></div>
                <div className="md:col-span-2"><Label>الوصف</Label><Input value={form.description} onChange={(e: any) => setForm({...form, description: e.target.value})} className={inputClass} placeholder="وصف حالة الدفع" /></div>
                <div><Label>اللون</Label><div className="flex gap-2"><input type="color" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} className="w-16 h-11 rounded-lg border cursor-pointer" /><Input value={form.color} onChange={(e: any) => setForm({...form, color: e.target.value})} className={inputClass} placeholder="#FFA500" /></div></div>
                <div><Label>ترتيب العرض</Label><Input type="number" value={form.sortOrder} onChange={(e: any) => setForm({...form, sortOrder: e.target.value})} className={inputClass} placeholder="1" /></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="psActive" checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} className="w-4 h-4" /><label htmlFor="psActive" className="text-sm font-medium">حالة نشطة</label></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">إلغاء</Button>
                <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">{editingStatus ? 'تحديث' : 'إضافة'}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedStatus && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">تفاصيل حالة الدفع</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 pb-4 border-b mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: selectedStatus.color || '#FFA500' }}><CreditCard className="w-8 h-8" /></div>
              <div><h3 className="text-xl font-bold">{selectedStatus.nameAr}</h3><p className="text-gray-500">{selectedStatus.name}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">الوصف</p><p className="font-semibold">{selectedStatus.description || '-'}</p></div>
              <div><p className="text-sm text-gray-500">اللون</p><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg border-2 border-gray-200" style={{ backgroundColor: selectedStatus.color || '#FFA500' }}></div><span className="font-mono">{selectedStatus.color || '-'}</span></div></div>
              <div><p className="text-sm text-gray-500">ترتيب العرض</p><p className="font-semibold">{selectedStatus.sortOrder}</p></div>
              <div><p className="text-sm text-gray-500">الحالة</p><span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedStatus.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{selectedStatus.isActive ? 'نشط' : 'غير نشط'}</span></div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedStatus); }} className="flex-1 bg-purple-600 hover:bg-purple-700"><Edit className="w-4 h-4 ml-2" />تعديل</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">إغلاق</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
